import { useState } from 'react';
import { MdInsertDriveFile, MdUpload } from 'react-icons/md';
import prettyBytes from 'pretty-bytes';
import toast from 'react-hot-toast';

import { MAX_CSV_BYTES } from '../constants/whatsapp';

interface CsvDropZoneProps {
  file: File | null;
  isParsing: boolean;
  onFileSelected: (file: File) => void;
  onFileCleared: () => void;
}

function isCsv(file: File): boolean {
  return file.name.toLowerCase().endsWith('.csv');
}

export default function CsvDropZone({
  file,
  isParsing,
  onFileSelected,
  onFileCleared,
}: CsvDropZoneProps) {
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const chosen = files[0];
    if (files.length > 1) {
      toast.error('Send one sheet at a time. Using the first one.');
    }
    if (!isCsv(chosen)) {
      toast.error(
        'That file is not a CSV. Save the sheet as CSV and try again.',
      );
      return;
    }
    if (chosen.size > MAX_CSV_BYTES) {
      toast.error(
        `That sheet is ${prettyBytes(chosen.size)}. The most we take is ${prettyBytes(MAX_CSV_BYTES)}.`,
      );
      return;
    }

    onFileSelected(chosen);
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
      return;
    }
    if (event.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    handleFiles(event.dataTransfer.files);
  };

  if (file && !isParsing) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2 overflow-hidden">
          <MdInsertDriveFile size={20} className="shrink-0 text-gray-400" />
          <div className="overflow-hidden">
            <div className="truncate text-sm font-medium text-gray-900">
              {file.name}
            </div>
            <div className="text-xs text-gray-500">
              {prettyBytes(file.size)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onFileCleared}
          className="shrink-0 rounded-md px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors focus-within:ring-2 focus-within:ring-blue-500 ${
        dragActive
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400'
      } ${isParsing ? 'pointer-events-none opacity-50' : ''}`}
    >
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="Recipients CSV"
        onChange={(event) => handleFiles(event.target.files)}
        className="absolute inset-0 cursor-pointer opacity-0"
        disabled={isParsing}
      />
      <MdUpload
        size={32}
        className={`mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`}
      />
      <p className="text-sm text-gray-600">
        {isParsing
          ? 'Reading the CSV…'
          : 'Drag & drop the recipients CSV here, or click to select'}
      </p>
      <p className="mt-1 text-xs text-gray-400">
        CSV, max {prettyBytes(MAX_CSV_BYTES)}
      </p>
    </div>
  );
}
